package org.teddapp.project.db.repo

import app.cash.sqldelight.coroutines.asFlow
import app.cash.sqldelight.coroutines.mapToList
import com.tedd.db.Pomodoro_item
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.withContext
import org.teddapp.project.db.newId
import org.teddapp.project.db.nowMillis

/**
 * Repository des items Pomodoro (spec §3.2, §8.2, §8.3, §8.4).
 * Les sources possibles : "action", "project_task", "exercise", "exercise_task", "quick".
 * Les doublons (même source dans la même session) sont rejetés silencieusement,
 * sauf pour "quick" qui a un source_id unique généré à chaque ajout (spec §11.4).
 */
class PomodoroItemRepository(private val db: TeddDatabase) {

    fun observeBySession(sessionId: String): Flow<List<Pomodoro_item>> =
        db.pomodoroItemQueries.selectBySession(sessionId).asFlow().mapToList(Dispatchers.Default)

    suspend fun listBySession(sessionId: String): List<Pomodoro_item> =
        withContext(Dispatchers.Default) {
            db.pomodoroItemQueries.selectBySession(sessionId).executeAsList()
        }

    suspend fun byId(id: String): Pomodoro_item? = withContext(Dispatchers.Default) {
        db.pomodoroItemQueries.selectById(id).executeAsOneOrNull()
    }

    /**
     * Ajoute un item depuis une source externe. Retourne l'id créé,
     * ou null si l'item existe déjà dans la session (doublon).
     */
    suspend fun addFromSource(
        sessionId: String,
        sourceType: String,
        sourceId: String,
        priority: Long = 4,
    ): String? = withContext(Dispatchers.Default) {
        // Doublon-check : un item live (non-supprimé) pour cette source existe déjà ?
        val existing = db.pomodoroItemQueries
            .selectBySourceInSession(sessionId, sourceType, sourceId)
            .executeAsOneOrNull()
        if (existing != null) return@withContext null

        val now = nowMillis()
        val id = newId("pi")
        val nextOrder = db.pomodoroItemQueries.maxOrderInSession(sessionId).executeAsOne() + 1
        db.pomodoroItemQueries.insert(
            id = id,
            session_id = sessionId,
            source_type = sourceType,
            source_id = sourceId,
            custom_title = null,
            priority = priority,
            order_index = nextOrder,
            completed = 0L,
            completed_at = null,
            estimated_minutes = null,
            created_at = now,
            updated_at = now,
        )
        id
    }

    /**
     * Ajoute une quick task (texte libre). Spec §8.3.
     * Génère un source_id aléatoire pour pouvoir avoir plusieurs items identiques sans collision.
     * [estimatedMinutes] est le temps qu'on se donne, facultatif.
     */
    suspend fun addQuick(
        sessionId: String,
        title: String,
        priority: Long = 4,
        estimatedMinutes: Long? = null,
    ): String = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val id = newId("pi")
        val quickId = newId("qk")
        val nextOrder = db.pomodoroItemQueries.maxOrderInSession(sessionId).executeAsOne() + 1
        db.pomodoroItemQueries.insert(
            id = id,
            session_id = sessionId,
            source_type = "quick",
            source_id = quickId,
            custom_title = title,
            priority = priority,
            order_index = nextOrder,
            completed = 0L,
            completed_at = null,
            estimated_minutes = estimatedMinutes,
            created_at = now,
            updated_at = now,
        )
        id
    }

    /** Corrige une tâche rapide : titre et temps prévu (null l'efface). Ne touche pas aux autres sources. */
    suspend fun updateQuick(id: String, title: String, minutes: Long?) = withContext(Dispatchers.Default) {
        val item = db.pomodoroItemQueries.selectById(id).executeAsOneOrNull() ?: return@withContext
        if (item.source_type != "quick") return@withContext
        db.pomodoroItemQueries.updateQuick(title, minutes, nowMillis(), id)
    }

    /** Change le temps prévu d'une tâche rapide ; null l'efface. */
    suspend fun setEstimatedMinutes(id: String, minutes: Long?) = withContext(Dispatchers.Default) {
        db.pomodoroItemQueries.updateEstimatedMinutes(minutes, nowMillis(), id)
    }

    /** Renvoie la nouvelle valeur completed (true/false) après bascule. La propagation
     *  vers la source est faite par PomodoroService.toggleItem. */
    suspend fun toggleCompletedRaw(id: String): Boolean = withContext(Dispatchers.Default) {
        val item = db.pomodoroItemQueries.selectById(id).executeAsOneOrNull() ?: return@withContext false
        val next = item.completed == 0L
        val now = nowMillis()
        db.pomodoroItemQueries.updateCompleted(
            completed = if (next) 1L else 0L,
            completed_at = if (next) now else null,
            updated_at = now,
            id = id,
        )
        next
    }

    suspend fun setPriority(id: String, priority: Long) = withContext(Dispatchers.Default) {
        db.pomodoroItemQueries.updatePriority(priority, nowMillis(), id)
    }

    /** Réordonnancement d'un item après drag (spec §8.6). */
    suspend fun reorder(sessionId: String, fromIndex: Int, toIndex: Int) =
        withContext(Dispatchers.Default) {
            if (fromIndex == toIndex) return@withContext
            val list = db.pomodoroItemQueries.selectBySession(sessionId).executeAsList().toMutableList()
            if (fromIndex !in list.indices) return@withContext
            val clampedTo = toIndex.coerceIn(0, list.lastIndex)
            val item = list.removeAt(fromIndex)
            list.add(clampedTo, item)
            val now = nowMillis()
            list.forEachIndexed { i, it ->
                if (it.order_index != i.toLong()) {
                    db.pomodoroItemQueries.updateOrder(i.toLong(), now, it.id)
                }
            }
        }

    /**
     * Fixe l'ordre manuel d'après une liste d'identifiants, dans l'ordre voulu. Seules les
     * lignes qui changent de rang sont réécrites.
     */
    suspend fun applyOrder(ids: List<String>) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        val current = ids.mapNotNull { db.pomodoroItemQueries.selectById(it).executeAsOneOrNull() }.associateBy { it.id }
        ids.forEachIndexed { i, id ->
            val row = current[id] ?: return@forEachIndexed
            if (row.order_index != i.toLong()) db.pomodoroItemQueries.updateOrder(i.toLong(), now, id)
        }
    }

    suspend fun softDelete(id: String) = withContext(Dispatchers.Default) {
        val now = nowMillis()
        db.pomodoroItemQueries.softDelete(now, now, id)
    }
}
